import React, { useState, useEffect, useRef } from 'react';
import { scanBusinessCard, searchPersonOrCompany } from './services/gemini';
import { ScanRecord, BusinessCard } from './types';
import Modal from './components/Modal';
import { Marked } from 'marked';

const marked = new Marked();

const App: React.FC = () => {
  const [records, setRecords] = useState<ScanRecord[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentSearchResult, setCurrentSearchResult] = useState<{title: string, html: string, raw: string} | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  // Load records from local storage
  useEffect(() => {
    const saved = localStorage.getItem('coko_records');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setRecords(parsed);
        if (parsed.length > 0) setSelectedRecordId(parsed[0].id);
      } catch (e) {
        console.error("Failed to parse local storage", e);
      }
    }
  }, []);

  // Save records to local storage
  useEffect(() => {
    localStorage.setItem('coko_records', JSON.stringify(records));
  }, [records]);

  const selectedRecord = records.find(r => r.id === selectedRecordId);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      await processImage(base64);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      setIsCameraActive(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      alert("無法啟動相機");
      setIsCameraActive(false);
    }
  };

  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(videoRef.current, 0, 0);
      const base64 = canvas.toDataURL('image/jpeg');
      
      // Stop stream
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      setIsCameraActive(false);
      
      processImage(base64);
    }
  };

  const processImage = async (base64: string) => {
    setIsScanning(true);
    try {
      const cards = await scanBusinessCard(base64);
      if (cards.length === 0) {
        alert("未能在圖片中偵測到名片！");
      } else {
        const newRecord: ScanRecord = {
          id: Date.now().toString(),
          timestamp: Date.now(),
          imageUrl: base64,
          cards: cards,
          searchCache: {}
        };
        setRecords(prev => [newRecord, ...prev]);
        setSelectedRecordId(newRecord.id);
        setIsScanModalOpen(false);
      }
    } catch (err) {
      alert("辨識失敗，請稍後再試。");
    } finally {
      setIsScanning(false);
    }
  };

  const handleInfoSearch = async (card: BusinessCard) => {
    const query = `${card.company} ${card.name}`;
    
    // Check Cache
    if (selectedRecord?.searchCache && selectedRecord.searchCache[query]) {
      const cached = selectedRecord.searchCache[query];
      setCurrentSearchResult({
        title: query,
        raw: cached,
        html: marked.parse(cached) as string
      });
      setIsSearchModalOpen(true);
      return;
    }

    setIsSearching(true);
    try {
      const result = await searchPersonOrCompany(query);
      const html = marked.parse(result.content) as string;
      
      setCurrentSearchResult({
        title: query,
        raw: result.content,
        html: html
      });
      
      // Update Cache
      setRecords(prev => prev.map(r => {
        if (r.id === selectedRecordId) {
          return {
            ...r,
            searchCache: { ...r.searchCache, [query]: result.content }
          };
        }
        return r;
      }));

      setIsSearchModalOpen(true);
    } catch (err) {
      alert("搜尋失敗");
    } finally {
      setIsSearching(false);
    }
  };

  const copyToClipboard = () => {
    if (currentSearchResult) {
      navigator.clipboard.writeText(currentSearchResult.raw);
      alert("已複製到剪貼簿！");
    }
  };

  const deleteRecord = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("確定要刪除此紀錄嗎？")) {
      setRecords(prev => prev.filter(r => r.id !== id));
      if (selectedRecordId === id) setSelectedRecordId(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-['Quicksand','Noto_Sans_TC'] bg-[#121212] text-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-[#1A1A1A]/80 backdrop-blur-md border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center shadow-lg shadow-pink-500/20">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <h1 className="text-xl font-bold tracking-tight bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
            COKO 名片紀錄
          </h1>
        </div>
        <button 
          onClick={() => setIsScanModalOpen(true)}
          className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white px-6 py-2.5 rounded-full font-bold shadow-lg shadow-pink-500/20 transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="hidden sm:inline">新增掃描</span>
        </button>
      </nav>

      {/* Main Content */}
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden h-[calc(100vh-76px)]">
        
        {/* Left Sidebar: History */}
        <aside className="w-full md:w-80 lg:w-96 border-r border-white/5 bg-[#161616] flex flex-col">
          <div className="p-4 border-b border-white/5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-widest">歷史紀錄</h2>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-3">
            {records.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center px-4 opacity-50">
                <div className="w-24 h-24 mb-4 animate-float opacity-30">
                  <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="100" cy="100" r="80" fill="#888" fillOpacity="0.1" />
                    <path d="M70 80C70 80 80 70 100 70C120 70 130 80 130 80" stroke="#888" strokeWidth="8" strokeLinecap="round" />
                    <circle cx="75" cy="110" r="8" fill="#888" />
                    <circle cx="125" cy="110" r="8" fill="#888" />
                  </svg>
                </div>
                <p>還沒有任何掃描紀錄唷！<br/>快按右上角的新增按鈕吧～</p>
              </div>
            ) : (
              records.map(record => (
                <div 
                  key={record.id}
                  onClick={() => setSelectedRecordId(record.id)}
                  className={`group relative p-3 rounded-super cursor-pointer transition-all border-2 ${
                    selectedRecordId === record.id 
                    ? 'bg-pink-500/10 border-pink-500/50' 
                    : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/10'
                  }`}
                >
                  <div className="flex gap-4 items-center">
                    <img src={record.imageUrl} className="w-16 h-16 object-cover rounded-2xl shadow-md" alt="Scan" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate text-pink-400">
                        {record.cards[0]?.name || "未知"}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {record.cards[0]?.company || "未知公司"}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {new Date(record.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={(e) => deleteRecord(record.id, e)}
                    className="absolute top-2 right-2 p-1.5 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Right Content: Details */}
        <section className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-[#121212]">
          {selectedRecord ? (
            <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
              <div className="relative group">
                <img 
                  src={selectedRecord.imageUrl} 
                  className="w-full max-h-[400px] object-contain rounded-super shadow-2xl bg-[#1E1E1E] border border-white/5" 
                  alt="Full Scan" 
                />
                <div className="absolute inset-0 rounded-super bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                  <span className="text-white/80 text-sm">點擊可放大預覽</span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {selectedRecord.cards.map((card, idx) => (
                  <div key={idx} className="bg-[#1E1E1E] p-6 rounded-super border border-white/5 shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-2 h-full bg-pink-500"></div>
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-1">{card.name || "未填寫"}</h3>
                        <p className="text-pink-400 font-medium">{card.title || "未填寫職稱"}</p>
                      </div>
                      <button 
                        onClick={() => handleInfoSearch(card)}
                        disabled={isSearching}
                        className="p-3 bg-white/5 hover:bg-pink-500/20 text-pink-400 rounded-full transition-colors disabled:opacity-50"
                        title="查看相關資訊"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-4 text-sm">
                      <div className="flex items-center gap-3 group">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-gray-400 group-hover:text-pink-400 group-hover:bg-pink-500/10 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        </div>
                        <span className="text-gray-300 font-medium">{card.company || "未填寫公司"}</span>
                      </div>
                      <div className="flex items-center gap-3 group">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-gray-400 group-hover:text-pink-400 group-hover:bg-pink-500/10 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        </div>
                        <span className="text-gray-300">{card.phone || "未提供"}</span>
                      </div>
                      <div className="flex items-center gap-3 group">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-gray-400 group-hover:text-pink-400 group-hover:bg-pink-500/10 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                        </div>
                        <span className="text-gray-300 truncate">{card.email || "未提供"}</span>
                      </div>
                      <div className="flex items-center gap-3 group">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-gray-400 group-hover:text-pink-400 group-hover:bg-pink-500/10 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        </div>
                        <span className="text-gray-300">{card.address || "未提供地址"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-6">
              <div className="w-48 h-48 bg-white/5 rounded-full flex items-center justify-center p-10 animate-float">
                 <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M40 60C40 48.9543 48.9543 40 60 40H140C151.046 40 160 48.9543 160 60V140C160 151.046 151.046 160 140 160H60C48.9543 160 40 151.046 40 140V60Z" fill="#F472B6" fillOpacity="0.2" />
                    <circle cx="100" cy="100" r="30" stroke="#F472B6" strokeWidth="6" strokeDasharray="12 8" />
                    <path d="M70 120C70 120 85 135 100 135C115 135 130 120 130 120" stroke="#F472B6" strokeWidth="6" strokeLinecap="round" />
                 </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">準備好掃描了嗎？</h3>
                <p className="text-gray-400 max-w-md">選擇左側的紀錄，或是點擊右上方的新增掃描按鈕來開始管理您的商務人脈吧！</p>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Scan Modal */}
      <Modal 
        isOpen={isScanModalOpen} 
        onClose={() => {
          setIsScanModalOpen(false);
          setIsCameraActive(false);
          if (videoRef.current?.srcObject) {
            (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
          }
        }} 
        title="新增掃描"
      >
        <div className="space-y-6">
          {isScanning ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <div className="relative w-24 h-24">
                <div className="absolute inset-0 border-4 border-pink-500/20 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-t-pink-500 rounded-full animate-spin"></div>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-pink-400 animate-pulse">AI 正在努力辨識中...</p>
                <p className="text-gray-500 mt-2">請耐心等候幾秒鐘</p>
              </div>
            </div>
          ) : isCameraActive ? (
            <div className="space-y-4">
              <div className="relative overflow-hidden rounded-super bg-black aspect-video">
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                <div className="absolute inset-0 border-[40px] border-black/40 pointer-events-none">
                   <div className="w-full h-full border-2 border-dashed border-pink-400 rounded-2xl"></div>
                </div>
              </div>
              <div className="flex gap-4">
                <button 
                  onClick={takePhoto}
                  className="flex-1 bg-pink-500 hover:bg-pink-400 py-3 rounded-full font-bold transition-colors shadow-lg shadow-pink-500/20"
                >
                  拍照
                </button>
                <button 
                  onClick={() => setIsCameraActive(false)}
                  className="px-8 bg-white/5 hover:bg-white/10 py-3 rounded-full font-bold transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="group flex flex-col items-center justify-center p-10 bg-white/5 hover:bg-pink-500/10 border-2 border-dashed border-white/10 hover:border-pink-500/50 rounded-super transition-all"
              >
                <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                   <svg className="w-8 h-8 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                </div>
                <p className="font-bold text-lg">從相簿選擇</p>
                <p className="text-sm text-gray-500 mt-2">支援 JPG/PNG</p>
              </button>
              <button 
                onClick={startCamera}
                className="group flex flex-col items-center justify-center p-10 bg-white/5 hover:bg-purple-500/10 border-2 border-dashed border-white/10 hover:border-purple-500/50 rounded-super transition-all"
              >
                <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                   <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <p className="font-bold text-lg">開啟相機拍照</p>
                <p className="text-sm text-gray-500 mt-2">自動偵測名片</p>
              </button>
            </div>
          )}
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
            accept="image/*" 
            className="hidden" 
          />
        </div>
      </Modal>

      {/* Search Result Modal */}
      <Modal 
        isOpen={isSearchModalOpen} 
        onClose={() => setIsSearchModalOpen(false)} 
        title={`${currentSearchResult?.title} 的背景資訊`}
      >
        <div className="space-y-6">
          <div className="flex justify-end">
            <button 
              onClick={copyToClipboard}
              className="flex items-center gap-2 text-pink-400 hover:text-pink-300 text-sm font-bold bg-pink-500/10 px-4 py-2 rounded-full transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
              複製內容
            </button>
          </div>
          <div 
            className="prose prose-invert prose-pink max-w-none text-gray-300 leading-relaxed space-y-4"
            dangerouslySetInnerHTML={{ __html: currentSearchResult?.html || "" }}
          />
        </div>
      </Modal>

      {/* Global Searching Indicator */}
      {isSearching && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md">
           <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto border-4 border-t-pink-500 border-white/10 rounded-full animate-spin"></div>
              <p className="text-xl font-bold text-pink-400">正在聯網搜尋中...</p>
           </div>
        </div>
      )}
    </div>
  );
};

export default App;
